import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Dropdownlist } from '@/components/Dropdownlist';
import { DateTimePicker } from '@/components/DateTimePicker';
import { NPSDContactInfo } from '@/components/npsd/NPSDContactInfo';

import moment from 'moment';
import { hasValue } from '@/utils/legacy-utils';

interface CodeItem {
  code: string | number;
  value: string;
}

interface IdTypeOption {
  value: number;
  text: string;
}

interface NPSDPersonDetailsProps {
  person: any;
  isEditMode?: boolean;
  genderCodes?: CodeItem[];
  raceCodes?: CodeItem[];
  eyeColorCodes?: CodeItem[];
  hairColorCodes?: CodeItem[];
  heightUnits?: CodeItem[];
  weightUnits?: CodeItem[];
  locStates?: CodeItem[];
  phoneOptionTypes?: CodeItem[];
  emailOptionTypes?: CodeItem[];
  showUsaStateInformation?: boolean;
  onUpdate?: (person: any) => void;
  onHeightUnitChange?: () => void;
  onWeightUnitChange?: () => void;
  onViewMap?: () => void;
}

const ID_TYPE_OPTIONS: IdTypeOption[] = [
  { value: 1, text: "Driver's License" },
  { value: 2, text: 'State ID' },
  { value: 3, text: 'Passport' },
  { value: 4, text: 'Military ID' },
  { value: 5, text: 'Other' },
];

export function NPSDPersonDetails({
  person,
  isEditMode = false,
  genderCodes = [],
  raceCodes = [],
  eyeColorCodes = [],
  hairColorCodes = [],
  heightUnits = [],
  weightUnits = [],
  locStates = [],
  phoneOptionTypes = [],
  emailOptionTypes = [],
  showUsaStateInformation = true,
  onUpdate,
  onHeightUnitChange,
  onWeightUnitChange,
  onViewMap,
}: NPSDPersonDetailsProps) {
  const { theme } = useTheme();

  const [localPerson, setLocalPerson] = useState<any>(null);
  const [dobPickerOpen, setDobPickerOpen] = useState(false);
  const [dlIssuedPickerOpen, setDlIssuedPickerOpen] = useState(false);
  const [dlExpiresPickerOpen, setDlExpiresPickerOpen] = useState(false);

  const isEmittingRef = useRef(false);

  // Sync local copy from person prop
  const syncFromPerson = useCallback(() => {
    if (person) {
      setLocalPerson(JSON.parse(JSON.stringify(person)));
    }
  }, [person]);

  // Initialize on mount
  useEffect(() => {
    syncFromPerson();
  }, [syncFromPerson]);

  // Watch person prop (skip if we just emitted)
  useEffect(() => {
    if (!isEmittingRef.current) {
      syncFromPerson();
    }
  }, [person, syncFromPerson]);

  // Emit update helper
  const emitUpdate = useCallback((updated: any) => {
    isEmittingRef.current = true;
    onUpdate?.(updated);
    // Reset flag on next tick
    setTimeout(() => {
      isEmittingRef.current = false;
    }, 0);
  }, [onUpdate]);

  // Field update helpers
  const updateField = useCallback((field: string, value: any) => {
    setLocalPerson((prev: any) => {
      if (!prev) return prev;
      const updated = { ...prev, [field]: value };
      emitUpdate(updated);
      return updated;
    });
  }, [emitUpdate]);

  const updateNestedField = useCallback((parent: string, field: string, value: any) => {
    setLocalPerson((prev: any) => {
      if (!prev) return prev;
      const updated = {
        ...prev,
        [parent]: { ...(prev[parent] || {}), [field]: value },
      };
      emitUpdate(updated);
      return updated;
    });
  }, [emitUpdate]);

  // Date handlers
  const onDobChange = useCallback((value: Date | null) => {
    setLocalPerson((prev: any) => {
      if (!prev) return prev;
      const updated = { ...prev, dob: value };
      emitUpdate(updated);
      return updated;
    });
  }, [emitUpdate]);

  const onDlIssuedChange = useCallback((value: Date | null) => {
    setLocalPerson((prev: any) => {
      if (!prev) return prev;
      const updated = { ...prev, driversLicenseIssued: value };
      emitUpdate(updated);
      return updated;
    });
  }, [emitUpdate]);

  const onDlExpiresChange = useCallback((value: Date | null) => {
    setLocalPerson((prev: any) => {
      if (!prev) return prev;
      const updated = { ...prev, driversLicenseExpires: value };
      emitUpdate(updated);
      return updated;
    });
  }, [emitUpdate]);

  // Date picker open handlers
  const openDatePicker = useCallback((pickerType: string) => {
    if (!isEditMode) return;

    window.dispatchEvent(new CustomEvent('datetime-picker-opened', {
      detail: { source: 'npsd-person-details' },
    }));

    setDobPickerOpen(false);
    setDlIssuedPickerOpen(false);
    setDlExpiresPickerOpen(false);

    switch (pickerType) {
      case 'dob':
        setDobPickerOpen(true);
        break;
      case 'dlIssued':
        setDlIssuedPickerOpen(true);
        break;
      case 'dlExpires':
        setDlExpiresPickerOpen(true);
        break;
    }
  }, [isEditMode]);

  // Handle other pickers opening
  useEffect(() => {
    const handleOtherPickerOpened = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail && detail.source !== 'npsd-person-details') {
        setDobPickerOpen(false);
        setDlIssuedPickerOpen(false);
        setDlExpiresPickerOpen(false);
      }
    };
    window.addEventListener('datetime-picker-opened', handleOtherPickerOpened);
    return () => {
      window.removeEventListener('datetime-picker-opened', handleOtherPickerOpened);
    };
  }, []);

  // Height/Weight unit changes
  const handleHeightUnitChange = useCallback((value: string | number | null) => {
    setLocalPerson((prev: any) => {
      if (!prev) return prev;
      const updated = {
        ...prev,
        height: { ...(prev.height || {}), displayUnits: value },
      };
      emitUpdate(updated);
      return updated;
    });
    onHeightUnitChange?.();
  }, [emitUpdate, onHeightUnitChange]);

  const handleWeightUnitChange = useCallback((value: string | number | null) => {
    setLocalPerson((prev: any) => {
      if (!prev) return prev;
      const updated = {
        ...prev,
        weight: { ...(prev.weight || {}), displayUnits: value },
      };
      emitUpdate(updated);
      return updated;
    });
    onWeightUnitChange?.();
  }, [emitUpdate, onWeightUnitChange]);

  // Contact handlers
  const onPhonesUpdate = useCallback((phones: any[]) => {
    setLocalPerson((prev: any) => {
      if (!prev) return prev;
      const updated = { ...prev, phoneNumbers: phones };
      emitUpdate(updated);
      return updated;
    });
  }, [emitUpdate]);

  const onEmailsUpdate = useCallback((emails: any[]) => {
    setLocalPerson((prev: any) => {
      if (!prev) return prev;
      const updated = { ...prev, emails: emails };
      emitUpdate(updated);
      return updated;
    });
  }, [emitUpdate]);

  // Location blur
  const onLocationBlur = useCallback(() => {
    if (localPerson) {
      emitUpdate(localPerson);
    }
  }, [localPerson, emitUpdate]);

  // Computed: age
  const age = useMemo((): string => {
    if (!localPerson?.dob) return '';
    if (typeof moment !== 'undefined') {
      return (moment as any).duration(moment().utc().diff(moment(localPerson.dob).utc())).format("Y");
    }
    return '';
  }, [localPerson?.dob]);

  // Computed: showDriversLicenseFields
  const showDriversLicenseFields = useMemo((): boolean => {
    return localPerson?.driversLicenseType === 1;
  }, [localPerson?.driversLicenseType]);

  // Computed: locationExists
  const locationExists = useMemo((): boolean => {
    return hasValue(localPerson?.locFormattedAddress) && localPerson.locFormattedAddress !== '';
  }, [localPerson?.locFormattedAddress]);

  // Computed: showHeightMeters
  const showHeightMeters = useMemo((): boolean => {
    return localPerson?.height?.displayUnits === 'm' || localPerson?.height?.displayUnits === 'meters';
  }, [localPerson?.height?.displayUnits]);

  // Format date for display
  const formatDate = useCallback((value: any): string => {
    if (!value) return '';
    const m = moment.utc(value);
    if (!m.isValid()) return '';
    return m.format('DD MMM YYYY');
  }, []);

  if (!localPerson) return null;

  return (
    <div className={`npsd-person-details npsd-entity-details${theme ? ' ' + theme : ''}`}>
      <div className="details-layout">
        {/* Main Column (left) */}
        <div className="details-column-main">
          {/* NAME Section */}
          <div className="panel name-panel">
            <div className="panel-header">NAME</div>
            <div className="panel-body">
              <div className="field-row">
                <div className="field-group field-half">
                  <label className="field-label required">First</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={localPerson.firstName || ''}
                    disabled={!isEditMode}
                    onChange={(e) => updateField('firstName', e.target.value)}
                  />
                </div>
                <div className="field-group field-half">
                  <label className="field-label required">Last</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={localPerson.lastName || ''}
                    disabled={!isEditMode}
                    onChange={(e) => updateField('lastName', e.target.value)}
                  />
                </div>
              </div>
              <div className="field-row">
                <div className="field-group field-third">
                  <label className="field-label">Middle</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={localPerson.middleName || ''}
                    disabled={!isEditMode}
                    onChange={(e) => updateField('middleName', e.target.value)}
                  />
                </div>
                <div className="field-group field-small">
                  <label className="field-label">Suffix</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={localPerson.suffix || ''}
                    disabled={!isEditMode}
                    placeholder="Jr, Sr, etc."
                    maxLength={10}
                    onChange={(e) => updateField('suffix', e.target.value)}
                  />
                </div>
                <div className="field-group field-half">
                  <label className="field-label">Aliases</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={localPerson.aliases || ''}
                    disabled={!isEditMode}
                    onChange={(e) => updateField('aliases', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* DESCRIPTION Section */}
          <div className="panel description-panel">
            <div className="panel-header">DESCRIPTION</div>
            <div className="panel-body">
              <div className="field-row">
                <div className="field-group field-half">
                  <label className="field-label">D.O.B.</label>
                  <DateTimePicker
                    value={localPerson.dob ? new Date(localPerson.dob) : null}
                    type="date"
                    appendToBody={true}
                    clearable={true}
                    disabled={!isEditMode}
                    open={dobPickerOpen}
                    popupClass={theme}
                    onChange={onDobChange}
                    onOpen={() => openDatePicker('dob')}
                    onOpenChange={(open) => { if (!open) setDobPickerOpen(false); }}
                  />
                </div>
                <div className="field-group field-half">
                  <label className="field-label">Age</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={age}
                    disabled
                  />
                </div>
              </div>
              <div className="field-row">
                <div className="field-group field-half">
                  <label className="field-label">Gender</label>
                  <Dropdownlist
                    value={localPerson.gender}
                    items={genderCodes}
                    labelKey="value"
                    valueKey="code"
                    placeholder="Select Gender"
                    searchable={false}
                    disabled={!isEditMode}
                    onChange={(val) => updateField('gender', val)}
                  />
                </div>
                <div className="field-group field-half">
                  <label className="field-label">Race</label>
                  <Dropdownlist
                    value={localPerson.race}
                    items={raceCodes}
                    labelKey="value"
                    valueKey="code"
                    placeholder="Select Race"
                    searchable={false}
                    disabled={!isEditMode}
                    onChange={(val) => updateField('race', val)}
                  />
                </div>
              </div>
              <div className="field-row">
                <div className="field-group field-half">
                  <label className="field-label">Eye Color</label>
                  <Dropdownlist
                    value={localPerson.eyeColor}
                    items={eyeColorCodes}
                    labelKey="value"
                    valueKey="code"
                    placeholder="Select Eye Color"
                    searchable={false}
                    disabled={!isEditMode}
                    onChange={(val) => updateField('eyeColor', val)}
                  />
                </div>
                <div className="field-group field-half">
                  <label className="field-label">Hair Color</label>
                  <Dropdownlist
                    value={localPerson.hairColor}
                    items={hairColorCodes}
                    labelKey="value"
                    valueKey="code"
                    placeholder="Select Hair Color"
                    searchable={false}
                    disabled={!isEditMode}
                    onChange={(val) => updateField('hairColor', val)}
                  />
                </div>
              </div>
              <div className="field-row">
                <div className="field-group field-half">
                  <label className="field-label">Height</label>
                  <Dropdownlist
                    value={localPerson.height?.displayUnits}
                    items={heightUnits}
                    labelKey="value"
                    valueKey="code"
                    placeholder="Select Unit"
                    searchable={false}
                    disabled={!isEditMode}
                    onChange={(val) => handleHeightUnitChange(val)}
                  />
                  {/* Meters input */}
                  {showHeightMeters && (
                    <div className="height-input">
                      <input
                        type="text"
                        className="kstc-input"
                        placeholder="Meters"
                        value={localPerson.personHeightMeters || ''}
                        disabled={!isEditMode}
                        onChange={(e) => updateField('personHeightMeters', e.target.value)}
                      />
                    </div>
                  )}
                  {/* Feet/Inches inputs */}
                  {!showHeightMeters && (
                    <div className="height-inputs-imperial">
                      <div className="height-input-group">
                        <span>Ft:</span>
                        <input
                          type="number"
                          className="kstc-input"
                          placeholder="Feet"
                          value={localPerson.personHeightFeet ?? ''}
                          disabled={!isEditMode}
                          onChange={(e) => updateField('personHeightFeet', e.target.value)}
                        />
                      </div>
                      <div className="height-input-group">
                        <span>In:</span>
                        <input
                          type="number"
                          className="kstc-input"
                          placeholder="Inches"
                          value={localPerson.personHeightInches ?? ''}
                          disabled={!isEditMode}
                          onChange={(e) => updateField('personHeightInches', e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="field-group field-half">
                  <label className="field-label">Weight</label>
                  <div className="unit-select">
                    <Dropdownlist
                      value={localPerson.weight?.displayUnits}
                      items={weightUnits}
                      labelKey="value"
                      valueKey="code"
                      placeholder="Select Unit"
                      searchable={false}
                      disabled={!isEditMode}
                      onChange={(val) => handleWeightUnitChange(val)}
                    />
                  </div>
                  <input
                    type="number"
                    className="kstc-input"
                    value={localPerson.personWeight ?? ''}
                    disabled={!isEditMode}
                    onChange={(e) => updateField('personWeight', e.target.value)}
                  />
                </div>
              </div>
              <div className="field-row">
                <div className="field-group field-full">
                  <label className="field-label">Notes</label>
                  <textarea
                    className="form-textarea"
                    value={localPerson.notes || ''}
                    disabled={!isEditMode}
                    onChange={(e) => updateField('notes', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* RESIDENCE Section */}
          <div className="panel residence-panel">
            <div className="panel-header">
              RESIDENCE
              {locationExists && (
                <button
                  className="kstc-btn kstc-btn--link"
                  onClick={onViewMap}
                >
                  View Map
                </button>
              )}
            </div>
            <div className="panel-body">
              <div className="field-row">
                <div className="field-group field-full">
                  <label className="field-label">Address</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={localPerson.locFormattedAddress || ''}
                    disabled={!isEditMode}
                    onChange={(e) => updateField('locFormattedAddress', e.target.value)}
                    onBlur={onLocationBlur}
                  />
                </div>
              </div>
              <div className="field-row">
                <div className="field-group field-half">
                  <label className="field-label">Apt/Suite</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={localPerson.locAddress2 || ''}
                    disabled={!isEditMode}
                    onChange={(e) => updateField('locAddress2', e.target.value)}
                  />
                </div>
                <div className="field-group field-half">
                  <label className="field-label">Building</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={localPerson.locName || ''}
                    disabled={!isEditMode}
                    onChange={(e) => updateField('locName', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Side Column (right) */}
        <div className="details-column-side">
          {/* CONTACT Section */}
          <NPSDContactInfo
            phones={localPerson.phoneNumbers}
            emails={localPerson.emails}
            isReadOnly={!isEditMode}
            phoneTypes={phoneOptionTypes}
            emailTypes={emailOptionTypes}
            title="CONTACT"
            onPhonesChange={onPhonesUpdate}
            onEmailsChange={onEmailsUpdate}
          />

          {/* GOVERNMENT ID Section */}
          <div className="panel govid-panel">
            <div className="panel-header">GOVERNMENT ID</div>
            <div className="panel-body">
              <div className="field-row">
                <div className="field-group field-full">
                  <label className="field-label">ID Type</label>
                  <Dropdownlist
                    value={localPerson.driversLicenseType}
                    items={ID_TYPE_OPTIONS}
                    labelKey="text"
                    valueKey="value"
                    placeholder="Select ID Type"
                    searchable={false}
                    disabled={!isEditMode}
                    onChange={(val) => updateField('driversLicenseType', val)}
                  />
                </div>
              </div>
              <div className="field-row">
                <div className="field-group field-full">
                  <label className="field-label">ID #</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={localPerson.driversLicenseNum || ''}
                    disabled={!isEditMode}
                    onChange={(e) => updateField('driversLicenseNum', e.target.value)}
                  />
                </div>
              </div>
              <div className="field-row">
                <div className="field-group field-full">
                  <label className="field-label">Issued</label>
                  <DateTimePicker
                    value={localPerson.driversLicenseIssued ? new Date(localPerson.driversLicenseIssued) : null}
                    type="date"
                    appendToBody={true}
                    clearable={true}
                    disabled={!isEditMode}
                    open={dlIssuedPickerOpen}
                    popupClass={theme}
                    onChange={onDlIssuedChange}
                    onOpen={() => openDatePicker('dlIssued')}
                    onOpenChange={(open) => { if (!open) setDlIssuedPickerOpen(false); }}
                  />
                </div>
              </div>
              <div className="field-row">
                <div className="field-group field-full">
                  <label className="field-label">Expiration</label>
                  <DateTimePicker
                    value={localPerson.driversLicenseExpires ? new Date(localPerson.driversLicenseExpires) : null}
                    type="date"
                    appendToBody={true}
                    clearable={true}
                    disabled={!isEditMode}
                    open={dlExpiresPickerOpen}
                    popupClass={theme}
                    onChange={onDlExpiresChange}
                    onOpen={() => openDatePicker('dlExpires')}
                    onOpenChange={(open) => { if (!open) setDlExpiresPickerOpen(false); }}
                  />
                </div>
              </div>
              {showUsaStateInformation && (
                <div className="field-row">
                  <div className="field-group field-full">
                    <label className="field-label">State/Country</label>
                    <Dropdownlist
                      value={localPerson.driversLicenseState}
                      items={locStates}
                      labelKey="value"
                      valueKey="code"
                      placeholder="Select State/Country"
                      disabled={!isEditMode}
                      onChange={(val) => updateField('driversLicenseState', val ?? '')}
                    />
                  </div>
                </div>
              )}
              {showDriversLicenseFields && (
                <div className="field-row">
                  <div className="field-group field-full">
                    <label className="field-label">Class</label>
                    <input
                      type="text"
                      className="kstc-input"
                      value={localPerson.driversLicenseClass || ''}
                      disabled={!isEditMode}
                      onChange={(e) => updateField('driversLicenseClass', e.target.value)}
                    />
                  </div>
                </div>
              )}
              {showDriversLicenseFields && (
                <div className="field-row">
                  <div className="field-group field-full">
                    <label className="field-label">Restriction(s)</label>
                    <textarea
                      className="form-textarea"
                      value={localPerson.driversLicenseRestrictions || ''}
                      disabled={!isEditMode}
                      onChange={(e) => updateField('driversLicenseRestrictions', e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* EMPLOYMENT Section */}
          <div className="panel employment-panel">
            <div className="panel-header">EMPLOYMENT</div>
            <div className="panel-body">
              <div className="field-row">
                <div className="field-group field-full">
                  <label className="field-label">Employer</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={localPerson.employer || ''}
                    disabled={!isEditMode}
                    onChange={(e) => updateField('employer', e.target.value)}
                  />
                </div>
              </div>
              <div className="field-row">
                <div className="field-group field-full">
                  <label className="field-label">Employee ID</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={localPerson.employeeId || ''}
                    disabled={!isEditMode}
                    placeholder="Badge #, Employee ID, etc."
                    onChange={(e) => updateField('employeeId', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

(window as any).NPSDPersonDetailsReact = NPSDPersonDetails;
